/**
 * VerificationQueue.js
 * Dashboard for managing field verification workflow
 * Displays issues needing verification, generates WhatsApp messages,
 * and tracks verification status
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../utils/ThemeSwitcher';
import {
  MessageSquare,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  Users,
  User,
  Filter,
  Search,
  Copy,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ExternalLink,
  Phone,
  Mail,
  FileText,
  Edit3,
  Check,
  X,
  MapPin,
  Hash,
  Calendar,
  BarChart3,
  Layers,
  Zap,
  Eye,
  EyeOff,
  Link,
  Target,
  Activity,
  Loader
} from 'lucide-react';

// Verification status types
const VERIFICATION_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  VERIFIED_VALID: 'verified_valid',
  VERIFIED_INVALID: 'verified_invalid',
  CORRECTED: 'corrected',
  NEEDS_INFO: 'needs_info'
};

// Issue severity colors
const SEVERITY_CONFIG = {
  critical: { color: '#EF4444', bg: '#FEE2E2', label: 'Critical' },
  warning: { color: '#F59E0B', bg: '#FEF3C7', label: 'Warning' },
  info: { color: '#2563EB', bg: '#DBEAFE', label: 'Info' }
};

// Issue type configuration
const ISSUE_TYPES = {
  outlier: { icon: AlertTriangle, label: 'Outlier', color: '#F59E0B' },
  duplicate: { icon: Copy, label: 'Duplicate', color: '#8B5CF6' },
  missing: { icon: AlertCircle, label: 'Missing', color: '#EF4444' },
  gibberish: { icon: MessageSquare, label: 'Gibberish', color: '#EC4899' },
  similar_response: { icon: Layers, label: 'Similar Response', color: '#F97316' },
  gps_issue: { icon: MapPin, label: 'GPS Issue', color: '#06B6D4' },
  duration: { icon: Clock, label: 'Duration', color: '#10B981' },
  skip_logic: { icon: Zap, label: 'Skip Logic', color: '#6366F1' },
  encoding: { icon: FileText, label: 'Encoding', color: '#78716C' },
  backcheck: { icon: RefreshCw, label: 'Back-check', color: '#0EA5E9' },
  // Coaching-loop detectors (sentinel/gated/probe/straight-lining)
  sentinel_phrase: { icon: MessageSquare, label: 'Short / Sentinel Response', color: '#EC4899' },
  gated_field:     { icon: Link,          label: 'Gated-Field Inconsistency', color: '#A855F7' },
  probe_depth:     { icon: Target,        label: 'Low-Specificity Response',  color: '#F97316' },
  straight_lining: { icon: Activity,      label: 'Straight-Lining Pattern',   color: '#DC2626' }
};

// API Base URL
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dataquadapp.com';

const VerificationQueue = ({ 
  checkResults = {},
  data = [],
  respondentIdField = null,
  collectorIdField = null,
  interviewDateField = null,
  onVerificationUpdate = null,
  verifiedExceptions = {},
  onExceptionsChange = null,
  // New props for session management
  projectId = null,
  projectName = 'Project',
  // Ship 3: correction-validation metadata sources
  columnTypes = {},     // { varName: 'numeric' | 'date' | 'string' | ... }
  xlsformData = null    // { parseResult, matchingResult, fileName } | null
}) => {
  const { isDarkMode } = useTheme();
  
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  
  // State
  const [activeTab, setActiveTab] = useState('queue');
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [filterCollector, setFilterCollector] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [verificationData, setVerificationData] = useState({});
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppTarget, setWhatsAppTarget] = useState('supervisor'); // 'supervisor' or 'collector'
  const [copiedMessage, setCopiedMessage] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50; // Show 50 items per page for performance
  
  // Archived issues - resolved issues hidden from queue but kept for cleaning
  const [archivedIssues, setArchivedIssues] = useState(() => {
    const saved = localStorage.getItem(`dataquad_archived_issues_${projectId || 'default'}`);
    return saved ? JSON.parse(saved) : {};
  });
  
  // Verification log - history of all resolutions
  const [verificationLog, setVerificationLog] = useState(() => {
    const saved = localStorage.getItem(`dataquad_verification_log_${projectId || 'default'}`);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [showVerificationLog, setShowVerificationLog] = useState(false);
  
  // Save archived issues and log to localStorage
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`dataquad_archived_issues_${projectId}`, JSON.stringify(archivedIssues));
    }
  }, [archivedIssues, projectId]);
  
  useEffect(() => {
    if (projectId) {
      localStorage.setItem(`dataquad_verification_log_${projectId}`, JSON.stringify(verificationLog));
    }
  }, [verificationLog, projectId]);
  
  // Session management state
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [verificationLink, setVerificationLink] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  
  // Session storage key
  const SESSION_STORAGE_KEY = `dataquad_verification_session_${projectId || 'default'}`;
  
  // Load session from localStorage on mount
  useEffect(() => {
    if (!projectId) return;
    
    const savedSession = localStorage.getItem(SESSION_STORAGE_KEY);
    if (savedSession) {
      try {
        const parsed = JSON.parse(savedSession);
        if (parsed.sessionId && parsed.expiresAt) {
          // Check if not expired
          if (new Date(parsed.expiresAt) > new Date()) {
            setActiveSession(parsed);
            setVerificationLink(parsed.verificationUrl);
            // console.log('🔄 Restored verification session from storage:', parsed.sessionId);
          } else {
            localStorage.removeItem(SESSION_STORAGE_KEY);
            // console.log('🕐 Removed expired session from storage');
          }
        }
      } catch (e) {
        console.error('Failed to parse saved session:', e);
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    }
  }, [projectId, SESSION_STORAGE_KEY]);
  
  // Auto-refresh when component mounts (modal opens)
  useEffect(() => {
    const doAutoRefresh = async () => {
      // Get session ID from localStorage directly
      const savedSession = localStorage.getItem(SESSION_STORAGE_KEY);
      if (!savedSession) {
        return;
      }
      
      let sessionId;
      try {
        const parsed = JSON.parse(savedSession);
        sessionId = parsed.sessionId;
        
        // Check if session is expired
        if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) {
          return;
        }
      } catch (e) {
        return;
      }
      
      if (!sessionId) return;
      
      setIsRefreshing(true);
      
      try {
        // Get auth headers inline
        let userEmail = '';
        try {
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
          userEmail = userInfo.email || '';
        } catch (e) {
          userEmail = localStorage.getItem('userEmail') || '';
        }
        
        const response = await fetch(`${API_BASE}/api/verification/sessions/${sessionId}/responses`, {
          headers: {
            'user_email': userEmail,
            'User-Email': userEmail,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.responses && typeof data.responses === 'object') {
            const newVerificationData = {};
            
            Object.entries(data.responses).forEach(([issueId, resp]) => {
              newVerificationData[issueId] = {
                status: resp.status,
                corrected_value: resp.corrected_value,
                notes: resp.notes,
                responded_at: resp.responded_at,
                responded_by: resp.responded_by
              };
            });
            
            setVerificationData(newVerificationData);
            
            // Notify parent of corrections for script generation
            if (onVerificationUpdate) {
              Object.entries(newVerificationData).forEach(([issueId, verifyData]) => {
                if (verifyData.status && verifyData.status !== 'pending') {
                  onVerificationUpdate(issueId, verifyData.status, verifyData.corrected_value);
                }
              });
            }
          }
          
          setLastRefreshed(new Date());
        } else if (response.status === 404) {
          // Session doesn't exist on server
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setActiveSession(null);
          setVerificationLink(null);
        }
      } catch (err) {
        console.error('Auto-refresh error:', err);
      } finally {
        setIsRefreshing(false);
      }
    };
    
    // Run on mount with small delay
    const timer = setTimeout(doAutoRefresh, 300);
    return () => clearTimeout(timer);
    
  }, [SESSION_STORAGE_KEY]); // Run on mount and if projectId changes
  
  // Get auth headers
  const getAuthHeaders = useCallback(() => {
    let userEmail = '';
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      userEmail = userInfo.email || '';
    } catch (e) {
      userEmail = localStorage.getItem('userEmail') || '';
    }
    return {
      'user_email': userEmail,
      'User-Email': userEmail,
      'Content-Type': 'application/json'
    };
  }, []);
  
  // Colors
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#111114' : '#FFFFFF',
    border: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
    borderStrong: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    text: isDarkMode ? '#E5E7EB' : '#222222',
    textSecondary: isDarkMode ? '#a8a49a' : '#555550',
    textMuted: isDarkMode ? '#6b6862' : '#908c82',
    primary: '#2563EB',
    ink: isDarkMode ? '#f0ede4' : '#1a1a1a',
    paper: isDarkMode ? '#0D0E0E' : '#F8F5EE',
    subtleBg: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
    success: '#10B981',
    warning: '#D97706',
    danger: '#EF4444',
    purple: '#8B5CF6',
    whatsapp: '#25D366',
  };
  
  // Extract all issues from check results
  const allIssues = useMemo(() => {
    // console.log('📊 Recalculating allIssues... verificationData keys:', Object.keys(verificationData).length);
    const issues = [];
    
    if (!checkResults) {
      // console.log('VerificationQueue: No checkResults provided');
      return issues;
    }
    
    // console.log('VerificationQueue: checkResults received:', Object.keys(checkResults));
    
    // Helper to get data from either 'overall' subobject or root level
    // Matches the same logic used in CleaningStudio
    const getCheckData = (check) => {
      if (!check) return null;
      // Handle nested structure: check.results.overall or check.overall or check
      if (check.results?.overall) return check.results.overall;
      if (check.overall) return check.overall;
      return check;
    };
    
    // Helper to get row data
    const getRowData = (rowIndex) => {
      if (!data || rowIndex >= data.length) return {};
      return data[rowIndex] || {};
    };

    // ────────────────────────────────────────────────────────────────────
    // Ship 3: extract correction-validation metadata for a given variable.
    // Defensively probes common XLSForm matchingResult shapes — returns
    // {} when nothing useful is found, so callers can spread the result
    // unconditionally onto issue objects without breaking pre-Ship-3 flows.
    //
    // Output keys (all optional):
    //   column_type         normalized type label from columnTypes prop
    //   xlsform_type        raw XLSForm type ("integer", "select_one yesno", …)
    //   xlsform_label       human-readable question label
    //   choices             [{ value, label }] for select_one/select_multiple
    //   constraint          raw XLSForm constraint expression
    //   constraint_message  supervisor-facing message from the form
    // ────────────────────────────────────────────────────────────────────
    const extractXlsformMeta = (varName) => {
      const meta = {};
      if (!varName) return meta;

      // Normalized column type (always available when columnTypes is provided)
      const ct = columnTypes && columnTypes[varName];
      if (ct) meta.column_type = String(ct).toLowerCase();

      if (!xlsformData) return meta;
      const matching = xlsformData.matchingResult || xlsformData.matching_result || {};
      const parseResult = xlsformData.parseResult || xlsformData.parse_result || {};

      // Probe possible shapes for the matched question:
      //   matching.matched / matched_questions / questions_by_name keyed by name
      //   matching.questions array
      //   parseResult.survey array
      let q = null;
      const namedDicts = [matching.matched, matching.matched_questions, matching.questions_by_name];
      for (const d of namedDicts) {
        if (d && typeof d === 'object' && !Array.isArray(d) && d[varName]) {
          q = d[varName];
          break;
        }
      }
      if (!q && Array.isArray(matching.questions)) {
        q = matching.questions.find(qq => qq?.name === varName || qq?.variable === varName);
      }
      if (!q && Array.isArray(parseResult.survey)) {
        q = parseResult.survey.find(qq => qq?.name === varName || qq?.variable === varName);
      }
      if (!q) return meta;

      const xlstype = q.type || q.xlsform_type;
      if (xlstype) meta.xlsform_type = String(xlstype);
      if (q.label) meta.xlsform_label = String(q.label);
      if (q.constraint) meta.constraint = String(q.constraint);
      if (q.constraint_message) meta.constraint_message = String(q.constraint_message);

      // Choices: either inline on the question, or in a list_name + choices sheet
      let choices = q.choices || q.choice_list || null;
      if (!choices && q.list_name) {
        const sheets = [parseResult.choices, matching.choices].filter(Array.isArray);
        for (const sheet of sheets) {
          const matched = sheet.filter(c => c && (c.list_name === q.list_name || c.listName === q.list_name));
          if (matched.length > 0) { choices = matched; break; }
        }
      }
      if (Array.isArray(choices) && choices.length > 0) {
        const norm = choices
          .map(c => {
            if (typeof c === 'string') return { value: c, label: c };
            const value = c?.name ?? c?.value;
            if (value === undefined || value === null) return null;
            return {
              value: String(value),
              label: String(c?.label ?? c?.title ?? value)
            };
          })
          .filter(Boolean);
        if (norm.length > 0) meta.choices = norm;
      }

      return meta;
    };

    // Track if we found any row-level data
    let hasRowLevelData = false;
    
    // 1. Statistical Outliers
    if (checkResults.outliers) {
      const outlierData = getCheckData(checkResults.outliers);
      // console.log('VerificationQueue: outlierData:', outlierData);
      
      if (outlierData) {
        // NEW: Check for aggregated detailed_issues from backend
        const allDetailedIssues = outlierData._all_detailed_issues || [];
        
        if (allDetailedIssues.length > 0) {
          // Use detailed_issues from backend (PREFERRED)
          hasRowLevelData = true;
          allDetailedIssues.forEach((detail, i) => {
            issues.push({
              id: `outlier_${detail.variable}_${detail.row_index}`,
              type: 'outlier',
              severity: 'warning',
              variable: detail.variable,
              rowIndex: detail.row_index,
              respondentId: detail.respondent_id || detail.row_index,
              collectorId: detail.collector_id,
              interviewDate: detail.interview_date,
              currentValue: detail.value,
              description: `Unusual value for ${detail.variable}`,
              details: `Value: ${detail.value ?? 'N/A'} (Statistical outlier)`,
              status: verificationData[`outlier_${detail.variable}_${detail.row_index}`]?.status || VERIFICATION_STATUS.PENDING
            });
          });
        } else {
          // Fallback: Process per-variable data
          Object.entries(outlierData).forEach(([variable, varData]) => {
            if (variable === 'overall' || variable === 'by_collector' || variable.startsWith('_')) return;
            
            // Check for per-variable detailed_issues (NEW from backend)
            if (varData?.detailed_issues && varData.detailed_issues.length > 0) {
              hasRowLevelData = true;
              varData.detailed_issues.forEach(detail => {
                issues.push({
                  id: `outlier_${variable}_${detail.row_index}`,
                  type: 'outlier',
                  severity: 'warning',
                  variable,
                  rowIndex: detail.row_index,
                  respondentId: detail.respondent_id || detail.row_index,
                  collectorId: detail.collector_id,
                  interviewDate: detail.interview_date,
                  currentValue: detail.value,
                  description: `Unusual value for ${variable}`,
                  details: `Value: ${detail.value ?? 'N/A'} (Statistical outlier)`,
                  status: verificationData[`outlier_${variable}_${detail.row_index}`]?.status || VERIFICATION_STATUS.PENDING
                });
              });
            }
            // Check for row-level indices (LEGACY support)
            else if (varData?.outlier_indices && varData.outlier_indices.length > 0) {
              hasRowLevelData = true;
              varData.outlier_indices.forEach((idx, i) => {
                const row = getRowData(idx);
                issues.push({
                  id: `outlier_${variable}_${idx}`,
                  type: 'outlier',
                  severity: 'warning',
                  variable,
                  rowIndex: idx,
                  respondentId: respondentIdField ? row[respondentIdField] : idx,
                  collectorId: collectorIdField ? row[collectorIdField] : null,
                  currentValue: varData.outlier_values?.[i] ?? row[variable],
                  description: `Unusual value for ${variable}`,
                  details: `Value: ${varData.outlier_values?.[i] ?? row[variable] ?? 'N/A'} (Statistical outlier)`,
                  status: verificationData[`outlier_${variable}_${idx}`]?.status || VERIFICATION_STATUS.PENDING
                });
              });
            } else if (varData?.outlier_count > 0) {
              // Fallback: Create summary issue when no row-level data
              issues.push({
                id: `outlier_${variable}_summary`,
                type: 'outlier',
                severity: 'warning',
                variable,
                rowIndex: null,
                respondentId: `${varData.outlier_count} outliers`,
                collectorId: null,
                currentValue: `${varData.outlier_count} values`,
                description: `${varData.outlier_count} outliers in ${variable}`,
                details: `Run quality checks with detailed output to see individual rows`,
                status: verificationData[`outlier_${variable}_summary`]?.status || VERIFICATION_STATUS.PENDING
              });
            }
          });
        }
      }
    }
    
    // 2. Duration Outliers
    if (checkResults.duration) {
      const durData = getCheckData(checkResults.duration);
      // console.log('VerificationQueue: durData:', durData);
      
      // NEW: Check for detailed_issues from backend
      if (durData?.detailed_issues && durData.detailed_issues.length > 0) {
        hasRowLevelData = true;
        durData.detailed_issues.forEach(detail => {
          issues.push({
            id: `duration_${detail.row_index}`,
            type: 'duration',
            severity: 'warning',
            variable: detail.variable || 'duration',
            rowIndex: detail.row_index,
            respondentId: detail.respondent_id || detail.row_index,
            collectorId: detail.collector_id,
            interviewDate: detail.interview_date,
            currentValue: detail.value,
            description: detail.duration_type === 'short' ? 'Interview too short' : 'Interview too long',
            details: `Duration: ${detail.value} (${detail.duration_type || 'outlier'})`,
            status: verificationData[`duration_${detail.row_index}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      }
      // LEGACY: Check for outlier_indices
      else if (durData?.outlier_indices && durData.outlier_indices.length > 0) {
        hasRowLevelData = true;
        durData.outlier_indices.forEach((idx, i) => {
          const row = getRowData(idx);
          issues.push({
            id: `duration_${idx}`,
            type: 'duration',
            severity: 'warning',
            variable: 'duration',
            rowIndex: idx,
            respondentId: respondentIdField ? row[respondentIdField] : idx,
            collectorId: collectorIdField ? row[collectorIdField] : null,
            currentValue: durData.outlier_values?.[i] || row.duration || row.interview_duration,
            description: 'Unusual interview duration',
            details: `Duration outside expected range`,
            status: verificationData[`duration_${idx}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      } else if (durData?.outlier_count > 0) {
        // Fallback summary for duration
        issues.push({
          id: `duration_summary`,
          type: 'duration',
          severity: 'warning',
          variable: 'duration',
          rowIndex: null,
          respondentId: `${durData.outlier_count} interviews`,
          collectorId: null,
          currentValue: `${durData.outlier_count} outliers`,
          description: `${durData.outlier_count} interviews with unusual duration`,
          details: `Short: ${durData.short_count || durData.short_duration_count || durData.too_short || 0}, Long: ${durData.long_count || durData.long_duration_count || durData.too_long || 0}`,
          status: verificationData[`duration_summary`]?.status || VERIFICATION_STATUS.PENDING
        });
      }
    }
    
    // 3. Duplicates
    if (checkResults.duplicates) {
      const dupData = getCheckData(checkResults.duplicates);
      // console.log('VerificationQueue: dupData:', dupData);
      
      let foundDupDetails = false;
      
      // NEW: Check for detailed_issues from backend (PREFERRED)
      if (dupData?.detailed_issues && dupData.detailed_issues.length > 0) {
        foundDupDetails = true;
        dupData.detailed_issues.forEach(detail => {
          issues.push({
            id: `duplicate_${detail.variable}_${detail.row_index}`,
            type: 'duplicate',
            severity: 'warning',
            variable: detail.variable,
            rowIndex: detail.row_index,
            respondentId: detail.respondent_id || detail.row_index,
            collectorId: detail.collector_id,
            interviewDate: detail.interview_date,
            currentValue: detail.value,
            description: `Duplicate value in ${detail.variable}`,
            details: `Value: ${detail.value} appears multiple times`,
            status: verificationData[`duplicate_${detail.variable}_${detail.row_index}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      }
      
      // LEGACY: duplicate_rows support
      if (!foundDupDetails && dupData?.duplicate_rows && dupData.duplicate_rows.length > 0) {
        foundDupDetails = true;
        dupData.duplicate_rows.forEach((dupRow, i) => {
          const idx = dupRow.index ?? dupRow.row_index ?? i;
          const row = getRowData(idx);
          issues.push({
            id: `duplicate_${idx}`,
            type: 'duplicate',
            severity: 'warning',
            variable: 'multiple',
            rowIndex: idx,
            respondentId: respondentIdField ? row[respondentIdField] : idx,
            collectorId: collectorIdField ? row[collectorIdField] : null,
            currentValue: dupRow.values || row,
            description: 'Potential duplicate record',
            details: `Duplicate on: ${dupRow.duplicate_on?.join(', ') || 'multiple fields'}`,
            status: verificationData[`duplicate_${idx}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      }
      
      // Also handle per_variable duplicates structure with duplicate_indices
      if (!foundDupDetails && dupData?.per_variable) {
        Object.entries(dupData.per_variable).forEach(([variable, varInfo]) => {
          if (varInfo?.duplicate_indices && varInfo.duplicate_indices.length > 0) {
            foundDupDetails = true;
            varInfo.duplicate_indices.forEach(idx => {
              const row = getRowData(idx);
              issues.push({
                id: `duplicate_${variable}_${idx}`,
                type: 'duplicate',
                severity: 'warning',
                variable,
                rowIndex: idx,
                respondentId: respondentIdField ? row[respondentIdField] : idx,
                collectorId: collectorIdField ? row[collectorIdField] : null,
                currentValue: row[variable],
                description: `Duplicate value in ${variable}`,
                details: `Value appears multiple times`,
                status: verificationData[`duplicate_${variable}_${idx}`]?.status || VERIFICATION_STATUS.PENDING
              });
            });
          }
        });
      }
      
      // Fallback summary for duplicates if no row-level data found
      if (!foundDupDetails && dupData?.total_duplicates > 0) {
        issues.push({
          id: `duplicate_summary`,
          type: 'duplicate',
          severity: dupData.total_duplicates > 100 ? 'critical' : 'warning',
          variable: 'multiple',
          rowIndex: null,
          respondentId: `${dupData.total_duplicates} values`,
          collectorId: null,
          currentValue: `${dupData.total_duplicates} duplicates`,
          description: `${dupData.total_duplicates} duplicate values across variables`,
          details: `Review duplicates in Uniqueness dashboard for details`,
          status: verificationData[`duplicate_summary`]?.status || VERIFICATION_STATUS.PENDING
        });
      }
    }
    
    // 4. Text Clustering - Similar Responses
    if (checkResults.text_clustering) {
      const textData = getCheckData(checkResults.text_clustering);
      // console.log('VerificationQueue: textData:', textData);
      
      const byVariable = textData?.by_variable || {};
      let foundTextDetails = false;
      
      Object.entries(byVariable).forEach(([variable, varData]) => {
        if (!varData) return;
        
        // Similar pairs
        if (varData.similar_pairs && Array.isArray(varData.similar_pairs) && varData.similar_pairs.length > 0) {
          foundTextDetails = true;
          varData.similar_pairs.forEach((pair, i) => {
            issues.push({
              id: `similar_${variable}_${i}`,
              type: 'similar_response',
              severity: pair.similarity >= 0.95 ? 'critical' : 'warning',
              variable,
              rowIndex: pair.response_1?.row_index,
              respondentId: pair.response_1?.respondent_id || pair.response_1?.collector_id || pair.response_1?.row_index,
              collectorId: pair.response_1?.collector_id || (pair.same_collector ? pair.response_1?.collector : null),
              interviewDate: pair.response_1?.interview_date,
              currentValue: pair.response_1?.text,
              description: `${(pair.similarity * 100).toFixed(0)}% similar to another response`,
              details: `Similar to ${pair.response_2?.respondent_id || 'row ' + pair.response_2?.row_index}: "${(pair.response_2?.text || '').substring(0, 50)}..."`,
              status: verificationData[`similar_${variable}_${i}`]?.status || VERIFICATION_STATUS.PENDING,
              relatedRow: pair.response_2?.row_index
            });
          });
        } else if (varData.similar_pair_count > 0) {
          foundTextDetails = true;
          issues.push({
            id: `similar_${variable}_summary`,
            type: 'similar_response',
            severity: 'warning',
            variable,
            rowIndex: null,
            respondentId: `${varData.similar_pair_count} pairs`,
            collectorId: null,
            currentValue: `${varData.similar_pair_count} pairs`,
            description: `${varData.similar_pair_count} similar pairs in ${variable}`,
            details: `Review in Text Analysis dashboard`,
            status: verificationData[`similar_${variable}_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
        
        // Outliers (text outliers)
        if (varData.outliers && Array.isArray(varData.outliers) && varData.outliers.length > 0) {
          foundTextDetails = true;
          varData.outliers.forEach((outlier, i) => {
            issues.push({
              id: `text_outlier_${variable}_${i}`,
              type: 'outlier',
              severity: 'info',
              variable,
              rowIndex: outlier.row_index,
              respondentId: outlier.collector || outlier.row_index,
              collectorId: outlier.collector,
              currentValue: outlier.text,
              description: 'Response doesn\'t fit typical patterns',
              details: `Distance score: ${outlier.distance_score?.toFixed(2) || 'N/A'}`,
              status: verificationData[`text_outlier_${variable}_${i}`]?.status || VERIFICATION_STATUS.PENDING
            });
          });
        } else if (varData.outlier_count > 0) {
          foundTextDetails = true;
          issues.push({
            id: `text_outlier_${variable}_summary`,
            type: 'outlier',
            severity: 'info',
            variable,
            rowIndex: null,
            respondentId: `${varData.outlier_count} responses`,
            collectorId: null,
            currentValue: `${varData.outlier_count} atypical`,
            description: `${varData.outlier_count} atypical responses in ${variable}`,
            details: `Review in Text Analysis dashboard`,
            status: verificationData[`text_outlier_${variable}_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
        
        // NEW: Gibberish detection
        const qualityIndicators = varData.quality_indicators || {};
        if (qualityIndicators.gibberish_detailed_issues && qualityIndicators.gibberish_detailed_issues.length > 0) {
          foundTextDetails = true;
          qualityIndicators.gibberish_detailed_issues.forEach((gibIssue, i) => {
            issues.push({
              id: `gibberish_${variable}_${gibIssue.row_index || i}`,
              type: 'gibberish',
              severity: 'warning',
              variable,
              rowIndex: gibIssue.row_index,
              respondentId: gibIssue.respondent_id || gibIssue.row_index,
              collectorId: gibIssue.collector_id,
              interviewDate: gibIssue.interview_date,
              currentValue: gibIssue.value,
              description: 'Possible gibberish/keyboard mashing',
              details: `Reason: ${gibIssue.reason || 'Pattern detected'}`,
              status: verificationData[`gibberish_${variable}_${gibIssue.row_index || i}`]?.status || VERIFICATION_STATUS.PENDING
            });
          });
        } else if (qualityIndicators.gibberish_count > 0) {
          // Fallback: use examples
          foundTextDetails = true;
          if (qualityIndicators.gibberish_examples && qualityIndicators.gibberish_examples.length > 0) {
            qualityIndicators.gibberish_examples.forEach((example, i) => {
              issues.push({
                id: `gibberish_${variable}_${example.row_index || i}`,
                type: 'gibberish',
                severity: 'warning',
                variable,
                rowIndex: example.row_index || null,
                respondentId: example.row_index || `Example ${i + 1}`,
                collectorId: null,
                currentValue: example.text,
                description: 'Possible gibberish/keyboard mashing',
                details: `Reason: ${example.reason || 'Pattern detected'}`,
                status: verificationData[`gibberish_${variable}_${example.row_index || i}`]?.status || VERIFICATION_STATUS.PENDING
              });
            });
          } else {
            // Summary only
            issues.push({
              id: `gibberish_${variable}_summary`,
              type: 'gibberish',
              severity: 'warning',
              variable,
              rowIndex: null,
              respondentId: `${qualityIndicators.gibberish_count} responses`,
              collectorId: null,
              currentValue: `${qualityIndicators.gibberish_count} gibberish`,
              description: `${qualityIndicators.gibberish_count} gibberish responses in ${variable}`,
              details: `Review in Text Analysis dashboard`,
              status: verificationData[`gibberish_${variable}_summary`]?.status || VERIFICATION_STATUS.PENDING
            });
          }
        }
      });
      
      // Summary fallback if no detailed data
      if (!foundTextDetails) {
        const totalSimilar = textData?.total_similar_pairs || 0;
        const totalOutliers = textData?.total_outliers || 0;
        
        if (totalSimilar > 0) {
          issues.push({
            id: `similar_summary`,
            type: 'similar_response',
            severity: totalSimilar > 20 ? 'critical' : 'warning',
            variable: 'text',
            rowIndex: null,
            respondentId: `${totalSimilar} pairs`,
            collectorId: null,
            currentValue: `${totalSimilar} similar pairs`,
            description: `${totalSimilar} highly similar response pairs`,
            details: `Review in Text Analysis dashboard`,
            status: verificationData[`similar_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
        
        if (totalOutliers > 0) {
          issues.push({
            id: `text_outlier_summary`,
            type: 'outlier',
            severity: 'info',
            variable: 'text',
            rowIndex: null,
            respondentId: `${totalOutliers} responses`,
            collectorId: null,
            currentValue: `${totalOutliers} atypical`,
            description: `${totalOutliers} atypical text responses`,
            details: `Review in Text Analysis dashboard`,
            status: verificationData[`text_outlier_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
      }
    }
    
    // 5. GPS Issues
    if (checkResults.geolocation) {
      const geoData = getCheckData(checkResults.geolocation);
      // console.log('VerificationQueue: geoData:', geoData);
      
      let foundGpsDetails = false;
      
      if (geoData?.suspicious_locations && Array.isArray(geoData.suspicious_locations) && geoData.suspicious_locations.length > 0) {
        foundGpsDetails = true;
        geoData.suspicious_locations.forEach((loc, i) => {
          const idx = loc.index ?? loc.row_index ?? i;
          const row = getRowData(idx);
          issues.push({
            id: `gps_${idx}`,
            type: 'gps_issue',
            severity: 'warning',
            variable: 'geolocation',
            rowIndex: idx,
            respondentId: respondentIdField ? row[respondentIdField] : idx,
            collectorId: collectorIdField ? row[collectorIdField] : null,
            currentValue: `${loc.latitude}, ${loc.longitude}`,
            description: loc.reason || 'Suspicious GPS location',
            details: `Distance from cluster: ${loc.distance_km?.toFixed(2) || 'N/A'} km`,
            status: verificationData[`gps_${idx}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      }
      // Also check for suspicious_clusters
      if (geoData?.suspicious_clusters && Array.isArray(geoData.suspicious_clusters) && geoData.suspicious_clusters.length > 0) {
        foundGpsDetails = true;
        geoData.suspicious_clusters.forEach((cluster, i) => {
          issues.push({
            id: `gps_cluster_${i}`,
            type: 'gps_issue',
            severity: 'warning',
            variable: 'geolocation',
            rowIndex: null,
            respondentId: `Cluster ${i + 1}`,
            collectorId: cluster.collectors?.join(', '),
            currentValue: `${cluster.latitude?.toFixed(4)}, ${cluster.longitude?.toFixed(4)}`,
            description: `${cluster.count || cluster.records || 'Multiple'} records at same location`,
            details: `GPS cluster - potential fabrication`,
            status: verificationData[`gps_cluster_${i}`]?.status || VERIFICATION_STATUS.PENDING
          });
        });
      }
      
      // Fallback summary for GPS
      if (!foundGpsDetails) {
        const clusterCount = geoData?.cluster_count || geoData?.suspicious_clusters?.length || 0;
        const suspiciousCount = geoData?.suspicious_count || geoData?.suspicious_locations?.length || 0;
        if (clusterCount > 0 || suspiciousCount > 0) {
          issues.push({
            id: `gps_summary`,
            type: 'gps_issue',
            severity: 'warning',
            variable: 'geolocation',
            rowIndex: null,
            respondentId: `${clusterCount + suspiciousCount} issues`,
            collectorId: null,
            currentValue: `${clusterCount} clusters, ${suspiciousCount} suspicious`,
            description: `${clusterCount + suspiciousCount} GPS issues detected`,
            details: `Review in Geolocation dashboard`,
            status: verificationData[`gps_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
      }
    }
    
    // 6. Survey Flow Violations
    if (checkResults.survey_flow) {
      const flowData = getCheckData(checkResults.survey_flow);
      // console.log('VerificationQueue: flowData:', flowData);
      
      const skipAnalysis = flowData?.skip_logic_analysis || flowData;
      const violations = skipAnalysis?.violations || [];
      
      if (Array.isArray(violations) && violations.length > 0) {
        violations.forEach((violation, i) => {
        const idx = violation.row_index ?? violation.row ?? i;
        const row = getRowData(idx);
        issues.push({
          id: `skip_logic_${i}`,
          type: 'skip_logic',
          severity: 'warning',
          variable: violation.variable || violation.question || 'unknown',
          rowIndex: idx,
          respondentId: respondentIdField ? row[respondentIdField] : `Row ${idx}`,
          collectorId: collectorIdField ? row[collectorIdField] : null,
          currentValue: violation.value,
          description: 'Skip logic violation',
          details: violation.message || `Value present when it should be skipped`,
          status: verificationData[`skip_logic_${i}`]?.status || VERIFICATION_STATUS.PENDING
        });
      });
      } else {
        // Fallback summary for survey flow
        const violationCount = skipAnalysis?.violation_count || flowData?.violation_count || flowData?.total_violations || 0;
        if (violationCount > 0) {
          issues.push({
            id: `skip_logic_summary`,
            type: 'skip_logic',
            severity: 'warning',
            variable: 'survey_flow',
            rowIndex: null,
            respondentId: `${violationCount} violations`,
            collectorId: null,
            currentValue: `${violationCount} violations`,
            description: `${violationCount} skip logic violations detected`,
            details: `Review in Survey Flow dashboard`,
            status: verificationData[`skip_logic_summary`]?.status || VERIFICATION_STATUS.PENDING
          });
        }
      }
    }
    
    // 7. Missingness - Summary for high missing variables
    if (checkResults.missingness) {
      const missData = getCheckData(checkResults.missingness);
      const perVar = missData?.per_variable || missData?.by_variable || {};
      
      let criticalCount = 0;
      let highCount = 0;
      
      Object.entries(perVar).forEach(([varName, varInfo]) => {
        const missingPct = varInfo?.missing_percent || varInfo?.missing_percentage || 0;
        if (missingPct > 20) criticalCount++;
        else if (missingPct > 10) highCount++;
      });
      
      if (criticalCount > 0 || highCount > 0) {
        issues.push({
          id: `missing_summary`,
          type: 'missing',
          severity: criticalCount > 0 ? 'critical' : 'warning',
          variable: 'multiple',
          rowIndex: null,
          respondentId: `${criticalCount + highCount} variables`,
          collectorId: null,
          currentValue: `${criticalCount} critical, ${highCount} high`,
          description: `${criticalCount + highCount} variables with high missing rates`,
          details: `Critical (>20%): ${criticalCount}, High (>10%): ${highCount}`,
          status: verificationData[`missing_summary`]?.status || VERIFICATION_STATUS.PENDING
        });
      }
    }

    // ────────────────────────────────────────────────────────────────────────
    // Coaching-loop detectors: sentinel_phrases, gated_fields, probe_depth,
    // straight_lining. Each emits a top-level `flagged_rows[]` with one entry
    // per offending row (after the `getCheckData` unwraps the `overall` key
    // that the v2 router adds). We surface them all as row-level issues so
    // the field team can verify or correct them per-respondent.
    // ────────────────────────────────────────────────────────────────────────

    // 8. Sentinel Phrases — short / placeholder open-ended responses
    if (checkResults.sentinel_phrases) {
      const sentData = getCheckData(checkResults.sentinel_phrases);
      const flagged = sentData?.flagged_rows || [];
      if (flagged.length > 0) hasRowLevelData = true;
      flagged.forEach(detail => {
        const id = `sentinel_${detail.variable}_${detail.row_index}`;
        const row = getRowData(detail.row_index);
        const wc = detail.word_count;
        issues.push({
          id,
          type: 'sentinel_phrase',
          severity: detail.severity || 'warning',
          variable: detail.variable,
          rowIndex: detail.row_index,
          respondentId: detail.respondent_id || (respondentIdField ? row[respondentIdField] : detail.row_index),
          collectorId: detail.collector_id || (collectorIdField ? row[collectorIdField] : null),
          interviewDate: detail.interview_date,
          currentValue: detail.response_preview ?? null,
          description: detail.message || `Short / sentinel response in ${detail.variable}`,
          details: `${detail.flag_type || 'sentinel'}${typeof wc === 'number' ? ` • ${wc} word${wc === 1 ? '' : 's'}` : ''}`,
          status: verificationData[id]?.status || VERIFICATION_STATUS.PENDING
        });
      });
    }

    // 9. Gated-Field Consistency — gate question vs follow-up contradictions
    if (checkResults.gated_fields) {
      const gateData = getCheckData(checkResults.gated_fields);
      const flagged = gateData?.flagged_rows || [];
      if (flagged.length > 0) hasRowLevelData = true;
      flagged.forEach(detail => {
        // For gate-rule flags: variable is the follow-up var. For consistency
        // rules (date_order/age_birth_year/numeric_range): no single variable —
        // fall back to rule_label.
        const variable =
          detail.follow_up_var ||
          detail.variable ||
          detail.rule_label ||
          detail.rule_id ||
          'gated_rule';
        const id = `gated_${detail.rule_id || variable}_${detail.row_index}`;
        const row = getRowData(detail.row_index);
        // Build a compact "details" line that explains the contradiction
        let detailsLine = detail.flag_type || detail.mode || 'gated';
        if (detail.gate_var) {
          detailsLine = `Gate ${detail.gate_var}=${detail.gate_value} → ${variable}`;
        } else if (detail.fields && typeof detail.fields === 'object') {
          const pairs = Object.entries(detail.fields)
            .map(([k, v]) => `${k}=${v}`)
            .join(', ');
          if (pairs) detailsLine = pairs;
        }
        issues.push({
          id,
          type: 'gated_field',
          severity: detail.severity || 'warning',
          variable,
          rowIndex: detail.row_index,
          respondentId: detail.respondent_id || (respondentIdField ? row[respondentIdField] : detail.row_index),
          collectorId: detail.collector_id || (collectorIdField ? row[collectorIdField] : null),
          interviewDate: detail.interview_date,
          currentValue: detail.response_preview ?? null,
          description: detail.message || `Gated-field rule violation: ${detail.rule_label || detail.rule_id || variable}`,
          details: detailsLine,
          status: verificationData[id]?.status || VERIFICATION_STATUS.PENDING
        });
      });
    }

    // 10. Probe Depth — low-specificity score on open-ended responses
    if (checkResults.probe_depth) {
      const probeData = getCheckData(checkResults.probe_depth);
      const flagged = probeData?.flagged_rows || [];
      if (flagged.length > 0) hasRowLevelData = true;
      flagged.forEach(detail => {
        const id = `probe_${detail.variable}_${detail.row_index}`;
        const row = getRowData(detail.row_index);
        const wc = detail.word_count;
        const score = typeof detail.score === 'number' ? detail.score : null;
        issues.push({
          id,
          type: 'probe_depth',
          severity: detail.severity || 'info',
          variable: detail.variable,
          rowIndex: detail.row_index,
          respondentId: detail.respondent_id || (respondentIdField ? row[respondentIdField] : detail.row_index),
          collectorId: detail.collector_id || (collectorIdField ? row[collectorIdField] : null),
          interviewDate: detail.interview_date,
          currentValue: detail.response_preview ?? null,
          description: detail.message || `Low-specificity response in ${detail.variable}`,
          details: [
            score !== null ? `Score ${score}` : null,
            typeof wc === 'number' ? `${wc} word${wc === 1 ? '' : 's'}` : null
          ].filter(Boolean).join(' • ') || 'probe_depth',
          status: verificationData[id]?.status || VERIFICATION_STATUS.PENDING
        });
      });
    }

    // 11. Straight-Lining — flatlined Likert responses (whole-respondent flag)
    if (checkResults.straight_lining) {
      const slData = getCheckData(checkResults.straight_lining);
      const flagged = slData?.flagged_rows || [];
      if (flagged.length > 0) hasRowLevelData = true;
      flagged.forEach(detail => {
        const id = `straightlining_${detail.row_index}`;
        const row = getRowData(detail.row_index);
        const methods = Array.isArray(detail.detected_by)
          ? detail.detected_by.join(', ')
          : (detail.detected_by || '');
        issues.push({
          id,
          type: 'straight_lining',
          severity: detail.severity || 'warning',
          // Whole-respondent flag — no single variable. Show a marker
          // string the existing UI can render verbatim in the variable cell.
          variable: '(whole respondent)',
          rowIndex: detail.row_index,
          respondentId: detail.respondent_id || (respondentIdField ? row[respondentIdField] : detail.row_index),
          collectorId: detail.collector_id || (collectorIdField ? row[collectorIdField] : null),
          interviewDate: detail.interview_date,
          currentValue: null,
          description: detail.message || `Flatlined response pattern across scale items`,
          details: methods ? `Detected by: ${methods}` : (detail.flag_type || 'straight_lining'),
          status: verificationData[id]?.status || VERIFICATION_STATUS.PENDING
        });
      });
    }
    
    // console.log('VerificationQueue: Total issues extracted:', issues.length);

    // Ship 3: enrich every issue with correction-validation metadata. Done
    // once at the end so individual branches don't need to know about it.
    // Skip the synthetic '(whole respondent)' variable for straight_lining —
    // there's no single column to validate against.
    const enriched = issues.map(issue => {
      const v = issue?.variable;
      if (!v || v === '(whole respondent)') return issue;
      const meta = extractXlsformMeta(v);
      return Object.keys(meta).length ? { ...issue, ...meta } : issue;
    });

    return enriched;
  }, [checkResults, data, respondentIdField, collectorIdField, verificationData, columnTypes, xlsformData]); // Ship 3: added columnTypes + xlsformData
  
  // Get unique collectors
  const collectors = useMemo(() => {
    const collectorSet = new Set();
    allIssues.forEach(issue => {
      if (issue.collectorId) collectorSet.add(issue.collectorId);
    });
    return Array.from(collectorSet).sort();
  }, [allIssues]);
  
  // Filter issues
  const filteredIssues = useMemo(() => {
    return allIssues.filter(issue => {
      // Exclude archived issues from queue view
      if (archivedIssues[issue.id]) return false;
      
      if (filterCollector !== 'all' && issue.collectorId !== filterCollector) return false;
      if (filterStatus !== 'all' && issue.status !== filterStatus) return false;
      if (filterType !== 'all' && issue.type !== filterType) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          issue.variable?.toLowerCase().includes(search) ||
          issue.description?.toLowerCase().includes(search) ||
          String(issue.respondentId).toLowerCase().includes(search) ||
          String(issue.currentValue).toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [allIssues, filterCollector, filterStatus, filterType, searchTerm, archivedIssues]);
  
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCollector, filterStatus, filterType, searchTerm]);
  
  // Paginated issues - only compute the current page
  const paginatedIssues = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredIssues.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredIssues, currentPage, ITEMS_PER_PAGE]);
  
  // Total pages
  const totalPages = Math.ceil(filteredIssues.length / ITEMS_PER_PAGE);
  
  // Group issues by collector
  const issuesByCollector = useMemo(() => {
    const grouped = {};
    filteredIssues.forEach(issue => {
      const collector = issue.collectorId || 'Unknown';
      if (!grouped[collector]) grouped[collector] = [];
      grouped[collector].push(issue);
    });
    return grouped;
  }, [filteredIssues]);
  
  // Statistics
  const stats = useMemo(() => {
    const byStatus = {};
    const byType = {};
    const byCollector = {};
    
    allIssues.forEach(issue => {
      byStatus[issue.status] = (byStatus[issue.status] || 0) + 1;
      byType[issue.type] = (byType[issue.type] || 0) + 1;
      const collector = issue.collectorId || 'Unknown';
      byCollector[collector] = (byCollector[collector] || 0) + 1;
    });
    
    return { byStatus, byType, byCollector, total: allIssues.length };
  }, [allIssues]);
  
  // Create verification session via API
  const createVerificationSession = async (collectorFilter = null) => {
    if (!projectId) {
      setSessionError('Project ID is required to create verification session');
      return null;
    }
    
    setIsCreatingSession(true);
    setSessionError(null);
    
    try {
      // Prepare issues for the session
      const issuesToInclude = collectorFilter 
        ? filteredIssues.filter(i => i.collectorId === collectorFilter)
        : filteredIssues;
      
      const pendingIssues = issuesToInclude.filter(i => i.status === VERIFICATION_STATUS.PENDING);
      
      const issuesPayload = pendingIssues.map(issue => ({
        id: issue.id,
        type: issue.type,
        severity: issue.severity,
        variable: issue.variable,
        row_index: issue.rowIndex,
        respondent_id: String(issue.respondentId),
        collector_id: issue.collectorId,
        current_value: issue.currentValue,
        description: issue.description,
        details: issue.details,
        // Ship 3: forward correction-validation metadata to the backend so the
        // public form can render smart inputs and submit_public_responses can
        // server-side validate. All optional — undefined keys serialize cleanly.
        column_type: issue.column_type,
        choices: issue.choices,
        min_value: issue.min_value,
        max_value: issue.max_value,
        xlsform_type: issue.xlsform_type,
        xlsform_label: issue.xlsform_label,
        constraint: issue.constraint,
        constraint_message: issue.constraint_message
      }));
      
      const response = await fetch(`${API_BASE}/api/verification/sessions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          project_id: projectId,
          project_name: projectName,
          issues: issuesPayload,
          collector_filter: collectorFilter,
          expires_days: 7
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Generate full verification URL
        const baseUrl = window.location.origin;
        const fullUrl = `${baseUrl}/verify/${data.session_id}`;
        
        const sessionData = {
          sessionId: data.session_id,
          verificationUrl: fullUrl,
          expiresAt: data.expires_at,
          issueCount: data.issue_count
        };
        
        setActiveSession(sessionData);
        setVerificationLink(fullUrl);
        
        // IMPORTANT: Save to localStorage for persistence
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
        
        // console.log('✅ Created verification session:', data.session_id);
        // console.log('📁 Saved session to localStorage');
        return fullUrl;
      } else {
        throw new Error(data.detail || 'Failed to create session');
      }
    } catch (err) {
      console.error('Error creating verification session:', err);
      setSessionError(err.message);
      return null;
    } finally {
      setIsCreatingSession(false);
    }
  };
  
  // Fetch latest verification responses from backend
  const refreshVerificationResponses = async () => {
    // Try multiple sources for session ID
    let sessionId = activeSession?.sessionId;
    
    if (!sessionId && verificationLink) {
      sessionId = verificationLink.split('/verify/')[1];
    }
    
    // Try localStorage as fallback
    if (!sessionId) {
      const savedSession = localStorage.getItem(SESSION_STORAGE_KEY);
      if (savedSession) {
        try {
          const parsed = JSON.parse(savedSession);
          sessionId = parsed.sessionId;
          // console.log('📂 Retrieved session from localStorage:', sessionId);
        } catch (e) {
          console.error('Failed to parse saved session');
        }
      }
    }
    
    if (!sessionId) {
      // console.log('❌ No active session to refresh');
      alert('No verification session found. Click "Generate WhatsApp Message" first to create a session.');
      return;
    }
    
    // console.log('🔄 Refreshing responses for session:', sessionId);
    setIsRefreshing(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/verification/sessions/${sessionId}/responses`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        // console.log('📥 Received verification data:', data);
        // console.log('📋 Response keys:', Object.keys(data.responses || {}));
        
        // Backend returns responses as a dict keyed by issue_id
        if (data.responses && typeof data.responses === 'object') {
          // Create completely new object to ensure React detects change
          const newVerificationData = {};
          
          // Copy existing data
          Object.keys(verificationData).forEach(key => {
            newVerificationData[key] = { ...verificationData[key] };
          });
          
          let updatedCount = 0;
          
          // Iterate over the responses dict
          Object.entries(data.responses).forEach(([issueId, resp]) => {
            // console.log(`  📝 Issue ${issueId}: status="${resp.status}", corrected="${resp.corrected_value}"`);
            
            // Store ALL responses, not just non-pending
            newVerificationData[issueId] = {
              status: resp.status,
              corrected_value: resp.corrected_value,
              notes: resp.notes,
              responded_at: resp.responded_at,
              responded_by: resp.responded_by
            };
            
            if (resp.status !== 'pending') {
              updatedCount++;
            }
          });
          
          // console.log(`Total responses: ${Object.keys(data.responses).length}, Non-pending: ${updatedCount}`);
          
          // Update state
          setVerificationData(newVerificationData);
          
          // Notify parent if callback exists
          if (onVerificationUpdate) {
            Object.entries(newVerificationData).forEach(([issueId, verifyData]) => {
              if (verifyData.status && verifyData.status !== 'pending') {
                onVerificationUpdate(issueId, verifyData.status, verifyData.corrected_value);
              }
            });
          }
        }
        
        setLastRefreshed(new Date());
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch verification responses:', response.status, errorText);
        alert(`Failed to refresh: ${response.status}`);
      }
    } catch (err) {
      console.error('❌ Error refreshing verification responses:', err);
      alert(`Error refreshing: ${err.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Generate WhatsApp message with verification link
  const generateWhatsAppMessage = (target, collectorFilter = null, linkUrl = null) => {
    const issues = collectorFilter 
      ? filteredIssues.filter(i => i.collectorId === collectorFilter)
      : filteredIssues;
    
    const pendingIssues = issues.filter(i => i.status === VERIFICATION_STATUS.PENDING);
    
    if (target === 'supervisor') {
      // Summary message for supervisor
      const byCollector = {};
      pendingIssues.forEach(issue => {
        const collector = issue.collectorId || 'Unknown';
        if (!byCollector[collector]) byCollector[collector] = { critical: 0, warning: 0, info: 0 };
        byCollector[collector][issue.severity]++;
      });
      
      let message = `📊 *DataQuad Quality Report*\n`;
      message += `📅 ${new Date().toLocaleDateString()}\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
      
      message += `*Summary:*\n`;
      message += `🔴 Critical: ${stats.byStatus.critical || 0}\n`;
      message += `🟡 Warning: ${pendingIssues.filter(i => i.severity === 'warning').length}\n`;
      message += `🔵 Info: ${pendingIssues.filter(i => i.severity === 'info').length}\n\n`;
      
      message += `*By Collector:*\n`;
      Object.entries(byCollector)
        .sort((a, b) => (b[1].critical + b[1].warning) - (a[1].critical + a[1].warning))
        .slice(0, 10)
        .forEach(([collector, counts]) => {
          const total = counts.critical + counts.warning + counts.info;
          const flag = counts.critical > 0 ? '⚠️' : '';
          message += `• ${collector}: ${total} issues ${flag}\n`;
        });
      
      // Add verification link if available
      if (linkUrl) {
        message += `\n📋 *Verify Issues:*\n${linkUrl}\n`;
        message += `\n_Click link to review and respond to issues_`;
      } else {
        message += `\n📋 Full report: [Generating link...]\n`;
        message += `\n_Reply with collector ID for details_`;
      }
      
      return message;
    } else {
      // Detailed message for specific collector
      let message = `📋 *Verification Needed*\n`;
      message += `Collector: ${collectorFilter || 'All'}\n`;
      message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
      
      pendingIssues.slice(0, 10).forEach((issue, idx) => {
        const num = idx + 1;
        const emoji = issue.severity === 'critical' ? '🔴' : issue.severity === 'warning' ? '🟡' : '🔵';
        
        message += `${num}️⃣ *Row ${issue.rowIndex ?? 'N/A'}* | ${issue.variable}\n`;
        message += `   ${emoji} ${issue.description}\n`;
        message += `   Value: \`${String(issue.currentValue).substring(0, 30)}\`\n\n`;
      });
      
      if (pendingIssues.length > 10) {
        message += `_...and ${pendingIssues.length - 10} more issues_\n\n`;
      }
      
      // Add verification link if available
      if (linkUrl) {
        message += `━━━━━━━━━━━━━━━━━━━━\n`;
        message += `📋 *Respond Online:*\n${linkUrl}\n`;
        message += `\n_Click to mark each issue as Valid or provide corrections_`;
      } else {
        message += `━━━━━━━━━━━━━━━━━━━━\n`;
        message += `*Reply format:* [#]-[OK/FIX]:[value]\n`;
        message += `Example: 1-OK or 2-FIX:John Smith`;
      }
      
      return message;
    }
  };
  
  // Handle verification status change
  const handleStatusChange = (issueId, newStatus, correction = null) => {
    setVerificationData(prev => ({
      ...prev,
      [issueId]: {
        status: newStatus,
        correction,
        timestamp: new Date().toISOString(),
        verifiedBy: 'current_user' // Would come from auth
      }
    }));
    
    // If marked as valid, add to exceptions
    if (newStatus === VERIFICATION_STATUS.VERIFIED_VALID && onExceptionsChange) {
      const issue = allIssues.find(i => i.id === issueId);
      if (issue) {
        onExceptionsChange(prev => ({
          ...prev,
          [issueId]: {
            type: issue.type,
            variable: issue.variable,
            rowIndex: issue.rowIndex,
            value: issue.currentValue,
            reason: 'Field verified as valid',
            verifiedAt: new Date().toISOString()
          }
        }));
      }
    }
    
    if (onVerificationUpdate) {
      onVerificationUpdate(issueId, newStatus, correction);
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedMessage(true);
    setTimeout(() => setCopiedMessage(false), 2000);
  };
  
  // Toggle selection
  const toggleSelection = (issueId) => {
    setSelectedIssues(prev => 
      prev.includes(issueId) 
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };
  
  // Select all visible
  const selectAllVisible = () => {
    const visibleIds = filteredIssues.map(i => i.id);
    setSelectedIssues(prev => {
      const allSelected = visibleIds.every(id => prev.includes(id));
      return allSelected ? prev.filter(id => !visibleIds.includes(id)) : [...new Set([...prev, ...visibleIds])];
    });
  };
  
  // Bulk status update
  const bulkUpdateStatus = (newStatus) => {
    selectedIssues.forEach(issueId => {
      handleStatusChange(issueId, newStatus);
    });
    setSelectedIssues([]);
  };
  
  // Archive resolved issues - hides from queue but keeps for cleaning script
  // Sync archive action with backend
  const syncArchiveWithBackend = async (issueIds, archive = true) => {
    // Get session ID
    let sessionId = activeSession?.sessionId;
    if (!sessionId) {
      const savedSession = localStorage.getItem(SESSION_STORAGE_KEY);
      if (savedSession) {
        try {
          const parsed = JSON.parse(savedSession);
          sessionId = parsed.sessionId;
        } catch (e) {}
      }
    }
    
    if (!sessionId) {
      // console.log('No session ID for backend sync');
      return false;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/verification/sessions/${sessionId}/archive`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          issue_ids: issueIds,
          archive: archive
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // console.log(`Backend sync: ${data.action} ${data.updated_count} issues`);
        return true;
      } else {
        console.error('Failed to sync archive with backend:', response.status);
        return false;
      }
    } catch (err) {
      console.error('Error syncing archive with backend:', err);
      return false;
    }
  };

  const archiveResolvedIssues = async () => {
    const resolvedIssues = allIssues.filter(issue => 
      issue.status === VERIFICATION_STATUS.CORRECTED || 
      issue.status === VERIFICATION_STATUS.VERIFIED_VALID
    );
    
    if (resolvedIssues.length === 0) {
      alert('No resolved issues to archive. Issues must be marked as "Corrected" or "Verified Valid" first.');
      return;
    }
    
    // Sync with backend first
    const issueIds = resolvedIssues.map(i => i.id);
    const synced = await syncArchiveWithBackend(issueIds, true);
    
    if (!synced) {
      const proceed = window.confirm('Could not sync with server. Archive locally only? (Supervisor will still see these issues)');
      if (!proceed) return;
    }
    
    const newArchived = { ...archivedIssues };
    const logEntries = [];
    
    resolvedIssues.forEach(issue => {
      const verification = verificationData[issue.id] || {};
      
      // Add to archived
      newArchived[issue.id] = {
        ...issue,
        archivedAt: new Date().toISOString(),
        verificationStatus: issue.status,
        correctedValue: verification.corrected_value,
        respondedBy: verification.responded_by,
        respondedAt: verification.responded_at,
        notes: verification.notes
      };
      
      // Add to log
      logEntries.push({
        id: `log_${Date.now()}_${issue.id}`,
        issueId: issue.id,
        type: issue.type,
        variable: issue.variable,
        rowIndex: issue.rowIndex,
        respondentId: issue.respondentId,
        originalValue: issue.currentValue,
        correctedValue: verification.corrected_value,
        status: issue.status,
        respondedBy: verification.responded_by,
        archivedAt: new Date().toISOString()
      });
    });
    
    setArchivedIssues(newArchived);
    setVerificationLog(prev => [...logEntries, ...prev]);
    
    const message = synced 
      ? `Archived ${resolvedIssues.length} resolved issues. They will be removed from the supervisor's view.`
      : `Archived ${resolvedIssues.length} issues locally. Note: Supervisor may still see these.`;
    alert(message);
  };
  
  // Archive selected issues
  const archiveSelectedIssues = async () => {
    const selectedResolved = allIssues.filter(issue => 
      selectedIssues.includes(issue.id) && 
      (issue.status === VERIFICATION_STATUS.CORRECTED || issue.status === VERIFICATION_STATUS.VERIFIED_VALID)
    );
    
    if (selectedResolved.length === 0) {
      alert('Selected issues must be "Corrected" or "Verified Valid" to archive.');
      return;
    }
    
    // Sync with backend first
    const issueIds = selectedResolved.map(i => i.id);
    const synced = await syncArchiveWithBackend(issueIds, true);
    
    if (!synced) {
      const proceed = window.confirm('Could not sync with server. Archive locally only?');
      if (!proceed) return;
    }
    
    const newArchived = { ...archivedIssues };
    const logEntries = [];
    
    selectedResolved.forEach(issue => {
      const verification = verificationData[issue.id] || {};
      
      newArchived[issue.id] = {
        ...issue,
        archivedAt: new Date().toISOString(),
        verificationStatus: issue.status,
        correctedValue: verification.corrected_value,
        respondedBy: verification.responded_by
      };
      
      logEntries.push({
        id: `log_${Date.now()}_${issue.id}`,
        issueId: issue.id,
        type: issue.type,
        variable: issue.variable,
        rowIndex: issue.rowIndex,
        originalValue: issue.currentValue,
        correctedValue: verification.corrected_value,
        status: issue.status,
        respondedBy: verification.responded_by,
        archivedAt: new Date().toISOString()
      });
    });
    
    setArchivedIssues(newArchived);
    setVerificationLog(prev => [...logEntries, ...prev]);
    setSelectedIssues([]);
    
    const message = synced 
      ? `Archived ${selectedResolved.length} issues. Supervisor will no longer see these.`
      : `Archived ${selectedResolved.length} issues locally.`;
    alert(message);
  };
  
  // Restore archived issue
  const restoreArchivedIssue = async (issueId) => {
    // Sync with backend
    await syncArchiveWithBackend([issueId], false);
    
    const newArchived = { ...archivedIssues };
    delete newArchived[issueId];
    setArchivedIssues(newArchived);
  };
  
  // Clear all archived (but keep log)
  const clearAllArchived = () => {
    if (window.confirm('This will restore all archived issues back to the queue. Continue?')) {
      setArchivedIssues({});
    }
  };

  // ============== COMPONENTS ==============
  
  const StatCard = ({ icon: Icon, label, value, color, subtitle }) => (
    <div style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '14px 16px',
      backgroundColor: colors.cardBg,
      borderRadius: '8px',
      border: `0.5px solid ${colors.border}`,
      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
      }}>
        <span style={{
          fontSize: '10px',
          fontWeight: '500',
          textTransform: 'lowercase',
          letterSpacing: '0.06em',
          opacity: 0.7,
          color: colors.textSecondary,
        }}>
          {label}
        </span>
        <Icon size={13} style={{ color, opacity: 0.85 }} />
      </div>
      <div style={{
        fontSize: '22px',
        fontWeight: '500',
        letterSpacing: '-0.02em',
        lineHeight: 1,
        color: colors.text,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      {subtitle && (
        <div style={{
          fontSize: '11px',
          color: colors.textMuted,
          opacity: 0.85,
        }}>
          {subtitle}
        </div>
      )}
    </div>
  );
  
  const IssueRow = ({ issue, isSelected, onSelect, onStatusChange, verificationData = {} }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showCorrection, setShowCorrection] = useState(false);
    const [correctionValue, setCorrectionValue] = useState('');
    
    // Get verification info for this issue
    const issueVerification = verificationData[issue.id] || {};
    const fieldCorrectedValue = issueVerification.corrected_value;
    const respondedBy = issueVerification.responded_by;
    const respondedAt = issueVerification.responded_at;
    
    const TypeIcon = ISSUE_TYPES[issue.type]?.icon || AlertCircle;
    const typeConfig = ISSUE_TYPES[issue.type] || { label: 'Unknown', color: '#6B7280' };
    const severityConfig = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
    
    const statusColors = {
      [VERIFICATION_STATUS.PENDING]: { bg: 'transparent', text: colors.textSecondary },
      [VERIFICATION_STATUS.SENT]: { bg: 'transparent', text: '#2563EB' },
      [VERIFICATION_STATUS.VERIFIED_VALID]: { bg: 'transparent', text: '#10B981' },
      [VERIFICATION_STATUS.VERIFIED_INVALID]: { bg: 'transparent', text: '#EF4444' },
      [VERIFICATION_STATUS.CORRECTED]: { bg: 'transparent', text: '#8B5CF6' },
      [VERIFICATION_STATUS.NEEDS_INFO]: { bg: 'transparent', text: '#D97706' }
    };
    
    const currentStatusColor = statusColors[issue.status] || statusColors[VERIFICATION_STATUS.PENDING];
    
    return (
      <div style={{
        backgroundColor: colors.cardBg,
        borderRadius: '6px',
        border: `0.5px solid ${isSelected ? colors.borderStrong : colors.border}`,
        marginBottom: '6px',
        overflow: 'hidden',
        transition: 'all 0.2s ease'
      }}>
        {/* Main row */}
        <div 
          style={{
            padding: '10px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Checkbox */}
          <div 
            onClick={(e) => { e.stopPropagation(); onSelect(issue.id); }}
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '3px',
              border: `1px solid ${isSelected ? colors.ink : colors.borderStrong}`,
              backgroundColor: isSelected ? colors.ink : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            {isSelected && <Check size={11} color={colors.paper} strokeWidth={2.5} />}
          </div>
          
          {/* Type icon — small inline, no filled badge */}
          <TypeIcon size={14} style={{ color: typeConfig.color, flexShrink: 0, opacity: 0.9 }} />
          
          {/* Info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              <span style={{ fontSize: '12px', fontWeight: '500', color: colors.text, letterSpacing: '-0.005em' }}>
                Row {issue.rowIndex}
              </span>
              <span style={{ fontSize: '11px', color: colors.textMuted, opacity: 0.6 }}>•</span>
              <span style={{ fontSize: '11px', color: colors.textSecondary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>
                {issue.variable}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {issue.description}
            </div>
          </div>
          
          {/* Severity badge — outlined chip */}
          <span style={{
            padding: '2px 8px',
            borderRadius: '100px',
            fontSize: '10px',
            fontWeight: '500',
            letterSpacing: '0.04em',
            textTransform: 'lowercase',
            backgroundColor: 'transparent',
            color: severityConfig.color,
            border: `0.5px solid ${severityConfig.color}40`,
          }}>
            {severityConfig.label}
          </span>
          
          {/* Status badge — outlined chip */}
          <span style={{
            padding: '2px 8px',
            borderRadius: '100px',
            fontSize: '10px',
            fontWeight: '500',
            letterSpacing: '0.04em',
            textTransform: 'lowercase',
            backgroundColor: 'transparent',
            color: currentStatusColor.text,
            border: `0.5px solid ${currentStatusColor.text}40`,
          }}>
            {issue.status.replace('_', ' ')}
          </span>
          
          {/* Collector */}
          {issue.collectorId && (
            <span style={{ fontSize: '11px', color: colors.textMuted, minWidth: '80px', fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em' }}>
              {issue.collectorId}
            </span>
          )}
          
          {/* Expand icon */}
          {isExpanded ? <ChevronUp size={14} style={{ color: colors.textMuted }} /> : <ChevronDown size={14} style={{ color: colors.textMuted }} />}
        </div>
        
        {/* Expanded details */}
        {isExpanded && (
          <div style={{
            padding: '14px',
            borderTop: `0.5px solid ${colors.border}`,
            backgroundColor: colors.subtleBg
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '10px', color: colors.textMuted, marginBottom: '6px', textTransform: 'lowercase', letterSpacing: '0.06em', fontWeight: 500 }}>current value</div>
                <div style={{ fontSize: '12px', color: colors.text, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', backgroundColor: colors.cardBg, padding: '8px 10px', borderRadius: '8px', border: `0.5px solid ${colors.border}` }}>
                  {String(issue.currentValue).substring(0, 100)}
                </div>
              </div>
              
              {/* Show corrected value from field team if available */}
              {issue.status === VERIFICATION_STATUS.CORRECTED && fieldCorrectedValue !== null && fieldCorrectedValue !== undefined && (
                <div>
                  <div style={{ fontSize: '10px', color: '#8B5CF6', marginBottom: '6px', fontWeight: '500', textTransform: 'lowercase', letterSpacing: '0.06em' }}>
                    field correction
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#8B5CF6', 
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', 
                    backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.06)', 
                    padding: '8px 10px', 
                    borderRadius: '8px',
                    border: '0.5px solid rgba(139, 92, 246, 0.3)'
                  }}>
                    {String(fieldCorrectedValue)}
                  </div>
                  {respondedBy && (
                    <div style={{ fontSize: '10px', color: colors.textMuted, marginTop: '4px', letterSpacing: '0.02em' }}>
                      By: {respondedBy} {respondedAt && `• ${new Date(respondedAt).toLocaleString()}`}
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <div style={{ fontSize: '10px', color: colors.textMuted, marginBottom: '6px', textTransform: 'lowercase', letterSpacing: '0.06em', fontWeight: 500 }}>details</div>
                <div style={{ fontSize: '12px', color: colors.text }}>{issue.details}</div>
              </div>
              {issue.respondentId && (
                <div>
                  <div style={{ fontSize: '10px', color: colors.textMuted, marginBottom: '6px', textTransform: 'lowercase', letterSpacing: '0.06em', fontWeight: 500 }}>respondent id</div>
                  <div style={{ fontSize: '12px', color: colors.text, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}>{issue.respondentId}</div>
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => onStatusChange(issue.id, VERIFICATION_STATUS.VERIFIED_VALID)}
                style={{
                  padding: '7px 14px',
                  borderRadius: '100px',
                  border: `0.5px solid ${colors.success}40`,
                  backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.06)',
                  color: colors.success,
                  fontSize: '11px',
                  fontWeight: '500',
                  letterSpacing: '-0.005em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'inherit',
                }}
              >
                <CheckCircle size={12} /> Mark Valid
              </button>
              <button
                onClick={() => setShowCorrection(!showCorrection)}
                style={{
                  padding: '7px 14px',
                  borderRadius: '100px',
                  border: `0.5px solid ${colors.purple}40`,
                  backgroundColor: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.06)',
                  color: colors.purple,
                  fontSize: '11px',
                  fontWeight: '500',
                  letterSpacing: '-0.005em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'inherit',
                }}
              >
                <Edit3 size={12} /> Provide Correction
              </button>
              <button
                onClick={() => onStatusChange(issue.id, VERIFICATION_STATUS.NEEDS_INFO)}
                style={{
                  padding: '7px 14px',
                  borderRadius: '100px',
                  border: `0.5px solid ${colors.warning}40`,
                  backgroundColor: isDarkMode ? 'rgba(217, 119, 6, 0.1)' : 'rgba(217, 119, 6, 0.06)',
                  color: colors.warning,
                  fontSize: '11px',
                  fontWeight: '500',
                  letterSpacing: '-0.005em',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontFamily: 'inherit',
                }}
              >
                <AlertCircle size={12} /> Needs More Info
              </button>
            </div>
            
            {/* Correction input */}
            {showCorrection && (
              <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
                <input
                  type="text"
                  value={correctionValue}
                  onChange={(e) => setCorrectionValue(e.target.value)}
                  placeholder="Enter corrected value..."
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: `0.5px solid ${colors.border}`,
                    backgroundColor: colors.cardBg,
                    color: colors.text,
                    fontSize: '12px',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  onClick={() => {
                    if (correctionValue) {
                      onStatusChange(issue.id, VERIFICATION_STATUS.CORRECTED, correctionValue);
                      setShowCorrection(false);
                      setCorrectionValue('');
                    }
                  }}
                  disabled={!correctionValue}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '100px',
                    border: 'none',
                    backgroundColor: correctionValue ? colors.ink : colors.border,
                    color: correctionValue ? colors.paper : colors.textMuted,
                    fontSize: '11px',
                    fontWeight: '500',
                    letterSpacing: '-0.005em',
                    cursor: correctionValue ? 'pointer' : 'not-allowed',
                    fontFamily: 'inherit',
                    opacity: correctionValue ? 1 : 0.6,
                  }}
                >
                  Save Correction
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  const WhatsAppModal = () => {
    const [localLink, setLocalLink] = useState(verificationLink);
    const [isGenerating, setIsGenerating] = useState(false);
    
    const currentMessage = generateWhatsAppMessage(
      whatsAppTarget, 
      filterCollector !== 'all' ? filterCollector : null,
      localLink
    );
    
    const handleGenerateLink = async () => {
      setIsGenerating(true);
      const link = await createVerificationSession(
        filterCollector !== 'all' ? filterCollector : null
      );
      if (link) {
        setLocalLink(link);
      }
      setIsGenerating(false);
    };
    
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MessageSquare size={24} style={{ color: '#25D366' }} />
              <h3 style={{ margin: 0, color: colors.text }}>WhatsApp Message</h3>
            </div>
            <button
              onClick={() => setShowWhatsAppModal(false)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X size={24} style={{ color: colors.textMuted }} />
            </button>
          </div>
          
          {/* Target toggle */}
          <div style={{ padding: '16px 24px', borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setWhatsAppTarget('supervisor')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: whatsAppTarget === 'supervisor' ? 'none' : `1px solid ${colors.border}`,
                  backgroundColor: whatsAppTarget === 'supervisor' ? colors.primary : 'transparent',
                  color: whatsAppTarget === 'supervisor' ? '#FFF' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Supervisor Summary
              </button>
              <button
                onClick={() => setWhatsAppTarget('collector')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: whatsAppTarget === 'collector' ? 'none' : `1px solid ${colors.border}`,
                  backgroundColor: whatsAppTarget === 'collector' ? colors.primary : 'transparent',
                  color: whatsAppTarget === 'collector' ? '#FFF' : colors.text,
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Collector Details
              </button>
            </div>
          </div>
          
          {/* Verification Link Section */}
          <div style={{ 
            padding: '16px 24px', 
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: isDarkMode ? '#1F1F23' : '#F0FDF4'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Link size={18} style={{ color: '#10B981' }} />
                <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text }}>
                  Verification Link
                </span>
              </div>
              
              {localLink ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
                  <input
                    type="text"
                    value={localLink}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: colors.cardBg,
                      color: colors.text,
                      fontSize: '12px'
                    }}
                  />
                  <button
                    onClick={() => copyToClipboard(localLink)}
                    style={{
                      padding: '8px',
                      borderRadius: '6px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: 'transparent',
                      cursor: 'pointer'
                    }}
                    title="Copy link"
                  >
                    <Copy size={14} style={{ color: colors.text }} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleGenerateLink}
                  disabled={isGenerating || isCreatingSession}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#10B981',
                    color: '#FFF',
                    cursor: isGenerating ? 'wait' : 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isGenerating ? 0.7 : 1
                  }}
                >
                  {isGenerating ? (
                    <>
                      <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Link size={14} />
                      Generate Link
                    </>
                  )}
                </button>
              )}
            </div>
            
            {sessionError && (
              <div style={{ 
                marginTop: '8px', 
                padding: '8px 12px', 
                backgroundColor: '#FEE2E2', 
                borderRadius: '6px',
                fontSize: '12px',
                color: '#DC2626'
              }}>
                {sessionError}
              </div>
            )}
            
            {localLink && (
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '12px', 
                color: colors.textSecondary 
              }}>
                ✓ Field team can respond without a DataQuad account. Link expires in 7 days.
              </p>
            )}
          </div>
          
          {/* Message preview */}
          <div style={{ flex: 1, padding: '18px', overflowY: 'auto' }}>
            <pre style={{
              margin: 0,
              padding: '16px',
              backgroundColor: isDarkMode ? '#1F1F23' : '#F3F4F6',
              borderRadius: '12px',
              fontSize: '13px',
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: colors.text,
              lineHeight: '1.6'
            }}>
              {currentMessage}
            </pre>
          </div>
          
          {/* Actions */}
          <div style={{
            padding: '16px 24px',
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => copyToClipboard(currentMessage)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Copy size={16} />
              {copiedMessage ? 'Copied!' : 'Copy Message'}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(currentMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#25D366',
                color: '#FFF',
                cursor: 'pointer',
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Send size={16} />
              Open WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  };

  // ============== MAIN RENDER ==============
  
  // Add CSS animation for refresh spinner
  const spinKeyframes = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  
  return (
    <div style={{ padding: isMobile ? '12px' : '24px', maxWidth: '1400px', margin: '0 auto', fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Inject spin animation */}
      <style>{spinKeyframes}</style>
      
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: isMobile ? '20px' : '22px', fontWeight: '500', color: colors.ink, letterSpacing: '-0.02em', margin: '0 0 4px 0' }}>
          Field Verification Queue
        </h2>
        <p style={{ fontSize: '12px', color: colors.textSecondary, margin: 0, opacity: 0.75 }}>
          Review data quality issues, send verification requests, and track field responses
        </p>
      </div>
      
      {/* Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(160px, 1fr))', 
        gap: '10px',
        marginBottom: '16px'
      }}>
        <StatCard 
          icon={AlertTriangle} 
          label="Total Issues" 
          value={stats.total} 
          color={colors.warning}
        />
        <StatCard 
          icon={Clock} 
          label="Pending" 
          value={stats.byStatus[VERIFICATION_STATUS.PENDING] || 0} 
          color={colors.textMuted}
        />
        <StatCard 
          icon={CheckCircle} 
          label="Verified Valid" 
          value={stats.byStatus[VERIFICATION_STATUS.VERIFIED_VALID] || 0} 
          color={colors.success}
        />
        <StatCard 
          icon={Edit3} 
          label="Corrected" 
          value={stats.byStatus[VERIFICATION_STATUS.CORRECTED] || 0} 
          color={colors.purple}
        />
        <StatCard 
          icon={Users} 
          label="Collectors" 
          value={collectors.length} 
          color={colors.primary}
        />
      </div>
      
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Search */}
        <div style={{
          flex: '1 1 200px',
          maxWidth: '300px',
          position: 'relative'
        }}>
          <Search size={13} style={{ 
            position: 'absolute', 
            left: '12px', 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: colors.textMuted,
            opacity: 0.7,
          }} />
          <input
            type="text"
            placeholder="Search issues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              borderRadius: '8px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: colors.subtleBg,
              color: colors.text,
              fontSize: '12px',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>
        
        {/* Filters */}
        <select
          value={filterCollector}
          onChange={(e) => setFilterCollector(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `0.5px solid ${colors.border}`,
            backgroundColor: colors.subtleBg,
            color: colors.text,
            fontSize: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          <option value="all">All Collectors</option>
          {collectors.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `0.5px solid ${colors.border}`,
            backgroundColor: colors.subtleBg,
            color: colors.text,
            fontSize: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          <option value="all">All Types</option>
          {Object.entries(ISSUE_TYPES).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '8px',
            border: `0.5px solid ${colors.border}`,
            backgroundColor: colors.subtleBg,
            color: colors.text,
            fontSize: '12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          <option value="all">All Status</option>
          {Object.entries(VERIFICATION_STATUS).map(([key, value]) => (
            <option key={key} value={value}>{value.replace('_', ' ')}</option>
          ))}
        </select>
        
        <div style={{ flex: 1 }} />
        
        {/* Last refreshed indicator */}
        {lastRefreshed && (
          <span style={{ fontSize: '11px', color: colors.textMuted, letterSpacing: '0.02em' }}>
            Updated {lastRefreshed.toLocaleTimeString()}
          </span>
        )}
        
        {/* Refresh button */}
        <button
          onClick={refreshVerificationResponses}
          disabled={isRefreshing}
          title="Refresh verification responses from field"
          style={{
            padding: '8px 14px',
            borderRadius: '100px',
            border: `0.5px solid ${colors.borderStrong}`,
            backgroundColor: colors.subtleBg,
            color: colors.text,
            fontSize: '12px',
            fontWeight: '500',
            letterSpacing: '-0.005em',
            cursor: isRefreshing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: isRefreshing ? 0.55 : 1,
            fontFamily: 'inherit',
            transition: 'all 0.2s ease',
          }}
        >
          <RefreshCw size={13} style={{ 
            animation: isRefreshing ? 'spin 1s linear infinite' : 'none' 
          }} />
          {isRefreshing ? 'Refreshing...' : 'Refresh Responses'}
        </button>
        
        {/* Actions — WhatsApp brand green, but refined as an outlined pill */}
        <button
          onClick={() => setShowWhatsAppModal(true)}
          style={{
            padding: '8px 14px',
            borderRadius: '100px',
            border: `0.5px solid ${colors.whatsapp}`,
            backgroundColor: isDarkMode ? 'rgba(37, 211, 102, 0.12)' : 'rgba(37, 211, 102, 0.08)',
            color: isDarkMode ? '#4AE383' : '#128C3E',
            fontSize: '12px',
            fontWeight: '500',
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontFamily: 'inherit',
            transition: 'all 0.2s ease',
          }}
        >
          <MessageSquare size={13} />
          Generate WhatsApp Message
        </button>
      </div>
      
      {/* Bulk actions */}
      {selectedIssues.length > 0 && (
        <div style={{
          padding: '10px 14px',
          backgroundColor: colors.subtleBg,
          borderRadius: '6px',
          border: `0.5px solid ${colors.border}`,
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '11px', color: colors.textSecondary, fontWeight: '500', letterSpacing: '0.04em', textTransform: 'lowercase' }}>
            {selectedIssues.length} selected
          </span>
          <button
            onClick={() => bulkUpdateStatus(VERIFICATION_STATUS.VERIFIED_VALID)}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.success}40`,
              backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.12)' : 'rgba(16, 185, 129, 0.08)',
              color: colors.success,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Mark All Valid
          </button>
          <button
            onClick={() => bulkUpdateStatus(VERIFICATION_STATUS.SENT)}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.primary}40`,
              backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
              color: colors.primary,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Mark as Sent
          </button>
          <button
            onClick={archiveSelectedIssues}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.borderStrong}`,
              backgroundColor: colors.subtleBg,
              color: colors.textSecondary,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            title="Archive resolved issues (keeps them in cleaning script)"
          >
            Archive Resolved
          </button>
          <button
            onClick={() => setSelectedIssues([])}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.textMuted,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Clear Selection
          </button>
        </div>
      )}
      
      {/* Archive/Log bar */}
      {(Object.keys(archivedIssues).length > 0 || verificationLog.length > 0) && (
        <div style={{
          padding: '8px 14px',
          backgroundColor: colors.subtleBg,
          border: `0.5px solid ${colors.border}`,
          borderRadius: '6px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{ fontSize: '11px', color: colors.textSecondary, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
              {Object.keys(archivedIssues).length} archived
            </span>
            <span style={{ fontSize: '11px', color: colors.textSecondary, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
              {verificationLog.length} in log
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setShowVerificationLog(true)}
              style={{
                padding: '4px 10px',
                borderRadius: '100px',
                border: `0.5px solid ${colors.borderStrong}`,
                backgroundColor: 'transparent',
                color: colors.text,
                fontSize: '11px',
                fontWeight: '500',
                letterSpacing: '-0.005em',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              View Log
            </button>
            {Object.keys(archivedIssues).length > 0 && (
              <button
                onClick={clearAllArchived}
                style={{
                  padding: '4px 10px',
                  borderRadius: '100px',
                  border: `0.5px solid ${colors.border}`,
                  backgroundColor: 'transparent',
                  color: colors.textMuted,
                  fontSize: '11px',
                  fontWeight: '500',
                  letterSpacing: '-0.005em',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Restore All
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Select all */}
      <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={selectAllVisible}
          style={{
            padding: '6px 12px',
            borderRadius: '100px',
            border: `0.5px solid ${colors.borderStrong}`,
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            fontSize: '11px',
            fontWeight: '500',
            letterSpacing: '-0.005em',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {filteredIssues.every(i => selectedIssues.includes(i.id)) ? 'Deselect All' : 'Select All'}
        </button>
        <span style={{ fontSize: '11px', color: colors.textMuted, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
          Showing {paginatedIssues.length} of {filteredIssues.length} issues (Page {currentPage} of {totalPages || 1})
        </span>
      </div>
      
      {/* Pagination Controls - Top */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          marginBottom: '12px',
          padding: '10px 14px',
          backgroundColor: colors.cardBg,
          borderRadius: '6px',
          border: `0.5px solid ${colors.border}`
        }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === 1 ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === 1 ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            ← Prev
          </button>
          <span style={{ padding: '0 12px', color: colors.textSecondary, fontWeight: '500', fontSize: '11px', letterSpacing: '0.04em', textTransform: 'lowercase', fontVariantNumeric: 'tabular-nums' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === totalPages ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            Next →
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === totalPages ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            Last
          </button>
        </div>
      )}
      
      {/* Issue list */}
      <div>
        {paginatedIssues.length === 0 ? (
          <div style={{
            padding: isMobile ? '32px 20px' : '60px 40px',
            textAlign: 'center',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            border: `0.5px solid ${colors.border}`
          }}>
            <CheckCircle size={36} style={{ color: colors.success, marginBottom: '14px', opacity: 0.85 }} />
            <h3 style={{ color: colors.text, marginBottom: '6px', fontSize: '15px', fontWeight: 500, letterSpacing: '-0.01em', margin: '0 0 6px 0' }}>No Issues Found</h3>
            <p style={{ color: colors.textSecondary, margin: 0, fontSize: '12px', opacity: 0.8 }}>
              {allIssues.length === 0 
                ? 'Run quality checks to identify issues for verification.'
                : 'No issues match your current filters.'}
            </p>
          </div>
        ) : (
          paginatedIssues.map(issue => (
            <IssueRow
              key={issue.id}
              issue={issue}
              isSelected={selectedIssues.includes(issue.id)}
              onSelect={toggleSelection}
              onStatusChange={handleStatusChange}
              verificationData={verificationData}
            />
          ))
        )}
      </div>
      
      {/* Pagination Controls - Bottom */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '16px',
          padding: '10px 14px',
          backgroundColor: colors.cardBg,
          borderRadius: '6px',
          border: `0.5px solid ${colors.border}`
        }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === 1 ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              opacity: currentPage === 1 ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            ← Previous
          </button>
          <span style={{ padding: '0 12px', color: colors.textSecondary, fontSize: '11px', letterSpacing: '0.04em', fontVariantNumeric: 'tabular-nums' }}>
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 12px',
              borderRadius: '100px',
              border: `0.5px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: currentPage === totalPages ? colors.textMuted : colors.text,
              fontSize: '11px',
              fontWeight: '500',
              letterSpacing: '-0.005em',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              opacity: currentPage === totalPages ? 0.45 : 1,
              fontFamily: 'inherit',
            }}
          >
            Next →
          </button>
        </div>
      )}
      
      {/* WhatsApp Modal */}
      {showWhatsAppModal && <WhatsAppModal />}
      
      {/* Verification Log Modal */}
      {showVerificationLog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            border: `0.5px solid ${colors.borderStrong}`,
            width: '90%',
            maxWidth: '800px',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
          }}>
            {/* Header */}
            <div style={{
              padding: '16px 20px',
              borderBottom: `0.5px solid ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '500', color: colors.ink, margin: 0, letterSpacing: '-0.01em' }}>
                  Verification Log
                </h3>
                <p style={{ fontSize: '12px', color: colors.textSecondary, margin: '4px 0 0 0', opacity: 0.75 }}>
                  History of all verified and corrected issues
                </p>
              </div>
              <button
                onClick={() => setShowVerificationLog(false)}
                style={{
                  padding: '8px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: colors.textMuted
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div style={{ 
              padding: '20px 24px', 
              overflowY: 'auto',
              flex: 1
            }}>
              {verificationLog.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  color: colors.textMuted 
                }}>
                  <FileText size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No verification history yet</p>
                  <p style={{ fontSize: '13px' }}>Archived issues will appear here</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '600px' : 'auto' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Row</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Variable</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Original</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Corrected</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>By</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: colors.textMuted, fontWeight: '600' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verificationLog.map((entry, idx) => (
                      <tr 
                        key={entry.id || idx}
                        style={{ 
                          borderBottom: `1px solid ${colors.border}`,
                          backgroundColor: idx % 2 === 0 ? 'transparent' : (isDarkMode ? '#1F1F23' : '#F9FAFB')
                        }}
                      >
                        <td style={{ padding: '10px 8px', fontSize: '13px', color: colors.text }}>
                          {entry.rowIndex}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '13px', color: colors.text, fontFamily: 'monospace' }}>
                          {entry.variable}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '13px', color: colors.textMuted, fontFamily: 'monospace' }}>
                          {String(entry.originalValue).substring(0, 20)}
                        </td>
                        <td style={{ 
                          padding: '10px 8px', 
                          fontSize: '13px', 
                          color: entry.status === 'corrected' ? '#8B5CF6' : colors.text,
                          fontFamily: 'monospace',
                          fontWeight: entry.status === 'corrected' ? '500' : '400'
                        }}>
                          {entry.correctedValue !== null && entry.correctedValue !== undefined 
                            ? String(entry.correctedValue).substring(0, 20)
                            : '—'}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '8px',
                            fontSize: '11px',
                            fontWeight: '500',
                            backgroundColor: entry.status === 'corrected' ? '#8B5CF620' : '#10B98120',
                            color: entry.status === 'corrected' ? '#8B5CF6' : '#10B981'
                          }}>
                            {entry.status === 'corrected' ? 'Corrected' : 'Valid'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '12px', color: colors.textSecondary }}>
                          {entry.respondedBy || '—'}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '12px', color: colors.textMuted }}>
                          {entry.archivedAt ? new Date(entry.archivedAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '13px', color: colors.textMuted }}>
                {verificationLog.length} entries
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {verificationLog.length > 0 && (
                  <button
                    onClick={() => {
                      // Export log as JSON
                      const dataStr = JSON.stringify(verificationLog, null, 2);
                      const blob = new Blob([dataStr], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `verification_log_${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      backgroundColor: 'transparent',
                      color: colors.text,
                      fontSize: '13px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Download size={14} />
                    Export Log
                  </button>
                )}
                <button
                  onClick={() => setShowVerificationLog(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: colors.primary,
                    color: '#FFF',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationQueue;